import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "../../../components/admin/PageHeader";
import { DataTable } from "../../../components/admin/DataTable";
import { FormModal } from "../../../components/admin/FormModal";
import { ConfirmDeleteModal } from "../../../components/admin/ConfirmDeleteModal";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { useCrudList } from "../../../hooks/useCrudList";
import { deliveryTimesService } from "../services/delivery-times";

export default function DeliveryTimesPage() {
  const { t } = useTranslation();
  const crud = useCrudList({
    service: deliveryTimesService,
    defaultSort: { key: "postcode", dir: "asc" },
    sessionKey: "deliveryTimes.pageSize",
  });
  const [formValue, setFormValue] = useState({ postcode: "", delay_days: 2, notes: "" });

  useEffect(() => {
    if (!crud.modalOpen) return;
    if (crud.editingRow) {
      setFormValue({
        postcode: crud.editingRow.postcode || "",
        delay_days: crud.editingRow.delay_days ?? 0,
        notes: crud.editingRow.notes || "",
      });
    } else {
      setFormValue({ postcode: "", delay_days: 2, notes: "" });
    }
  }, [crud.modalOpen, crud.editingRow]);

  const columns = [
    { key: "postcode", labelKey: "admin.delivery_times.col_postcode", sortable: true, className: "w-32" },
    {
      key: "delay_days",
      labelKey: "admin.delivery_times.col_delay",
      sortable: true,
      className: "w-32",
      render: (r) => (
        <span className="font-mono text-sm text-text-primary">
          {r.delay_days} {t("admin.delivery_times.days_suffix")}
        </span>
      ),
    },
    {
      key: "notes",
      labelKey: "admin.delivery_times.col_notes",
      render: (r) => (
        <span className="line-clamp-2 text-text-secondary">{r.notes || "—"}</span>
      ),
    },
  ];

  return (
    <>
      <div className="space-y-6" data-testid="delivery-times-page">
        <PageHeader
          title={t("admin.delivery_times.title")}
          subtitle={t("admin.delivery_times.subtitle")}
          actionLabel={t("admin.common.add_new")}
          onAction={crud.openCreate}
          actionTestId="delivery-times-add-new"
        />
        <DataTable
          columns={columns}
          items={crud.items}
          loading={crud.loading}
          total={crud.total}
          page={crud.page}
          pageSize={crud.pageSize}
          onPageChange={crud.setPage}
          onPageSizeChange={crud.setPageSize}
          sortKey={crud.sortKey}
          sortDir={crud.sortDir}
          onSortChange={crud.changeSort}
          searchValue={crud.searchValue}
          onSearchChange={crud.setSearchValue}
          onEdit={crud.openEdit}
          onDelete={crud.askDelete}
          entityTestId="delivery-times"
        />
      </div>

      <FormModal
        open={crud.modalOpen}
        onClose={crud.closeModal}
        mode={crud.editingRow ? "edit" : "create"}
        entityLabel={t("admin.delivery_times.entity_singular")}
        saving={crud.saving}
        onSubmit={() =>
          crud.save({
            postcode: formValue.postcode.trim(),
            delay_days: Number(formValue.delay_days),
            notes: formValue.notes?.trim() || null,
          })
        }
        testId="delivery-times-form"
        submitTestId="delivery-times-form-submit"
        cancelTestId="delivery-times-form-cancel"
      >
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="dt-postcode">{t("admin.delivery_times.field_postcode")}</Label>
            <Input
              id="dt-postcode"
              value={formValue.postcode}
              onChange={(e) => setFormValue({ ...formValue, postcode: e.target.value })}
              required
              minLength={3}
              maxLength={10}
              autoFocus
              data-testid="delivery-times-form-postcode"
              className="bg-surface border-border-subtle font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dt-delay">{t("admin.delivery_times.field_delay")}</Label>
            <Input
              id="dt-delay"
              type="number"
              value={formValue.delay_days}
              onChange={(e) => setFormValue({ ...formValue, delay_days: e.target.value })}
              required
              min={0}
              max={30}
              step={1}
              data-testid="delivery-times-form-delay"
              className="bg-surface border-border-subtle font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dt-notes">{t("admin.delivery_times.field_notes")}</Label>
            <Textarea
              id="dt-notes"
              value={formValue.notes}
              onChange={(e) => setFormValue({ ...formValue, notes: e.target.value })}
              maxLength={200}
              rows={2}
              data-testid="delivery-times-form-notes"
              className="bg-surface border-border-subtle"
            />
          </div>
        </div>
      </FormModal>

      <ConfirmDeleteModal
        open={Boolean(crud.deleteRow)}
        onClose={crud.cancelDelete}
        onConfirm={crud.confirmDelete}
        name={crud.deleteRow?.postcode || ""}
        busy={crud.deleting}
        testId="delivery-times-delete"
      />
    </>
  );
}
